import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiHeart, FiMapPin, FiGift, FiSettings, FiDollarSign, FiArrowRight, FiCheckCircle, FiClock, FiShield, FiPhone } = FiIcons;

const Services = () => {
  const services = [
    {
      id: 'property-care',
      icon: FiHome,
      title: 'Property Care',
      description: 'Comprehensive property management and maintenance services for your homes in India.',
      features: [
        'Regular property inspections',
        'Maintenance coordination',
        'Security monitoring',
        'Utility bill management',
        'Tenant management',
        'Emergency repairs'
      ],
      pricing: 'Starting from ₹2,999/month',
      popular: true
    },
    {
      id: 'parental-care',
      icon: FiHeart,
      title: 'Parental Care',
      description: 'Compassionate care and support services for your elderly parents and family members.',
      features: [
        'Health monitoring',
        'Medical appointment assistance',
        'Daily check-ins',
        'Emergency support',
        'Medication reminders',
        'Companion services'
      ],
      pricing: 'Starting from ₹4,999/month'
    },
    {
      id: 'travel-services',
      icon: FiMapPin,
      title: 'Travel Services',
      description: 'End-to-end travel planning and support for your visits to India and family trips.',
      features: [
        'Trip planning & itinerary',
        'Accommodation booking',
        'Airport transfers',
        'Local transportation',
        'Tourist guide services',
        'Travel insurance'
      ],
      pricing: 'Starting from ₹1,999/trip'
    },
    {
      id: 'celebrate',
      icon: FiGift,
      title: 'Celebrate',
      description: 'Make your loved ones\' special occasions memorable with our celebration services.',
      features: [
        'Event planning & coordination',
        'Gift selection & delivery',
        'Surprise arrangements',
        'Virtual celebration support',
        'Catering & decoration',
        'Photography & videography'
      ],
      pricing: 'Starting from ₹3,999/event'
    },
    {
      id: 'financial-advisory',
      icon: FiDollarSign,
      title: 'Financial Advisory',
      description: 'Trusted financial advice and planning services tailored for NRIs.',
      features: [
        'Investment planning',
        'Tax advisory',
        'Retirement planning',
        'Wealth management',
        'Estate planning',
        'Risk assessment'
      ],
      pricing: 'Starting from ₹5,999/consultation'
    },
    {
      id: 'custom-services',
      icon: FiSettings,
      title: 'Custom Services',
      description: 'Need something unique? Tell us your requirements and we will create a personalized solution tailored specifically for you.',
      features: [
        'Customized NRI assistance',
        'Legal & documentation support',
        'Property-related special requests',
        'Personal errands & coordination',
        'Business support services',
        'Dedicated relationship manager'
      ],
      pricing: 'Custom Pricing'
    },
  ];

  const additionalServices = [
    { icon: FiShield, title: 'Emergency Support', description: '24/7 emergency assistance' },
    { icon: FiPhone, title: 'Concierge Services', description: 'Personal assistance for various needs' },
    { icon: FiClock, title: 'Scheduled Services', description: 'Regular maintenance and check-ups' },
    { icon: FiCheckCircle, title: 'Quality Assurance', description: 'Regular quality monitoring' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 sm:py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive local support services designed specifically for NRIs. 
            We handle everything so you can focus on what matters most.
          </p>
        </motion.div>

        {/* Main Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`bg-white rounded-xl shadow-lg border-2 p-6 sm:p-8 relative flex flex-col justify-between ${
                service.popular ? 'border-primary-500' : 'border-gray-200'
              }`}
            >
              {service.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={service.icon} className="text-2xl sm:text-3xl text-primary-600" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">What's Included:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 text-sm flex-shrink-0" />
                        <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mt-auto">
                <div className="text-center mb-4">
                  <p className="text-sm sm:text-base font-semibold text-primary-600">Pay as you choose the service</p>
                </div>
                <Link
                  to={`/${service.id}`}
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <span>Learn More</span>
                  <SafeIcon icon={FiArrowRight} className="text-sm" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 mb-12 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">
            Additional Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <div key={index} className="text-center p-2">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <SafeIcon icon={service.icon} className="text-xl text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base">{service.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-primary-600 rounded-xl p-6 sm:p-10 text-center text-white"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-base sm:text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
            Choose the service that best fits your needs or contact us for a custom solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/login"
              className="w-full sm:w-auto bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base"
            >
              Request Service
            </Link>
            <Link
              to="/login"
              className="w-full sm:w-auto border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors text-center text-sm sm:text-base"
            >
              Sign Up Now
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;